import { ChangeEvent, DragEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, LockSimple, UploadSimple } from "phosphor-react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { useIdentityVerificationPanel } from "@contexts/identityVerificationPanel/IdentityVerificationPanelProvider";
import useIdentityVerification from "@hooks/user/useIdentityVerification";
import CameraCapture from "@elements/CameraCapture";
import { Button, color, Feedback, font, Link } from "@runwayhealth/runway-components-react";
import { cleanUserError } from "@store/user/userSlice";

// Kept at 100MB (VERIFY-R2) to match the backend's actual Express upload cap —
// the design mock's dropzone copy said 10MB, but that number doesn't match any
// real server-side limit, so the cap here is deliberately not what the mock shows.
const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

const ACCEPTED_MIME_TYPES = ["image/jpeg", "image/png", "application/pdf"];

const isAcceptedFileType = (file: File) => ACCEPTED_MIME_TYPES.includes(file.type);

const validateFile = (file: File): string | null => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return "That file is too large. Please choose one under 100MB.";
  }
  if (!isAcceptedFileType(file)) {
    return "That file type isn't supported. Please choose a JPG, PNG, or PDF.";
  }
  return null;
};

// Hosts the ID photo capture and shows the chosen file name — keep the panel
// out of PostHog session replay.
const Container = styled.div.attrs({
  className: "ph-no-capture",
})`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
`;

const Eyebrow = styled.p`
  font-family: ${font.family};
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin: 0;
  color: ${color.brand.primary.medium};
`;

const Title = styled.h3`
  font-family: ${font.family};
  font-weight: 700;
  font-size: 1.5rem;
  margin: 0;
  color: ${color.brand.secondary.dark};
`;

const Description = styled.p`
  margin: 0;
  line-height: 1.5;
`;

const Checklist = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChecklistItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${color.brand.secondary.dark};

  svg {
    color: ${color.feedback.success.medium};
    flex-shrink: 0;
  }
`;

const Dropzone = styled.div<{ $isDragActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border: 2px dashed
    ${({ $isDragActive }) => ($isDragActive ? color.brand.primary.medium : color.brand.secondary.light)};
  border-radius: 8px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;

  svg {
    color: ${color.brand.secondary.medium};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const DropzoneTitle = styled.p`
  margin: 0;
  font-weight: 700;
  color: ${color.brand.secondary.dark};
`;

const DropzoneSubtext = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: ${color.brand.secondary.medium};
`;

const CameraToggle = styled.button`
  align-self: center;
  font-family: ${font.family};
  font-size: 0.875rem;
  color: ${color.brand.secondary.medium};
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HipaaNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: ${color.feedback.info.light};
  border-radius: 8px;
  padding: 12px 16px;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid ${color.brand.secondary.light};
`;

const IdentityVerificationPanelContent = () => {
  const dispatch = useDispatch();
  const { isOpen, close } = useIdentityVerificationPanel();
  const { isSubmitting, error, success, submitPhotoId } = useIdentityVerification();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isWebcamMode, setIsWebcamMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Tracks whether we've actually observed a pending phase, so completion is
  // detected by a real true -> false transition rather than "isSubmitting is
  // currently false" — the latter would also match the render right after
  // clicking submit, before the saga's userPending() has landed.
  const wasSubmittingRef = useRef(false);

  // Auto-closes and clears once the upload+attach calls succeed (VERIFY-R6/E7).
  // Runs regardless of the sync call's outcome, since userMutationSuccess (and
  // with it success.type === "write") fires before the sync call even starts.
  useEffect(() => {
    if (isSubmitting) {
      wasSubmittingRef.current = true;
      return;
    }
    if (!wasSubmittingRef.current) return;
    wasSubmittingRef.current = false;
    if (success.type === "write" && !error.message) {
      setSelectedFile(null);
      close();
    }
  }, [isSubmitting, success, error, close]);

  // The panel can be dismissed via this component's own Cancel link, but also
  // via OverlayPanel's X button or backdrop click — both call the shared
  // context's close() directly and never touch this component's local state.
  // OverlayPanel also renders inside a Slide unmountOnExit, whose actual
  // unmount is deferred to a CSS transition timer, not synchronous — so a
  // live camera stream left open by any of those paths wouldn't get cleaned
  // up by unmount alone. Keying off the panel's own isOpen flag instead
  // covers the Cancel link, the X, the backdrop, and the submit-success
  // auto-close uniformly, regardless of which one fired.
  useEffect(() => {
    if (!isOpen) {
      setIsWebcamMode(false);
    }
  }, [isOpen]);

  const handleFileSelected = (file: File | undefined) => {
    if (!file) return;
    const validationError = validateFile(file);
    if (validationError) {
      setFileError(validationError);
      return;
    }
    setFileError(null);
    setSelectedFile(file);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFileSelected(e.target.files?.[0]);
    // Allow re-selecting the same file later (VERIFY-E4 doesn't fire an onChange otherwise).
    e.target.value = "";
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    handleFileSelected(e.dataTransfer.files?.[0]);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = () => setIsDragActive(false);

  const handleBrowseClick = () => fileInputRef.current?.click();

  const handleUseCameraClick = () => {
    setFileError(null);
    setIsWebcamMode(true);
  };

  // Thin adapter, not a parallel path: a captured photo goes through the
  // exact same handleFileSelected (and from there, the same validate/stage
  // pipeline) as a browsed or dropped file.
  const handleWebcamCapture = (file: File) => {
    setIsWebcamMode(false);
    handleFileSelected(file);
  };

  const handleWebcamCancel = () => setIsWebcamMode(false);

  const handleSubmit = () => {
    if (!selectedFile || isSubmitting) return;
    submitPhotoId(selectedFile);
  };

  // Clears any leftover server error from a previous session as soon as this
  // one mounts — before paint, via useLayoutEffect, so a stale error never
  // flashes. This panel can be dismissed via Cancel, the X button, or the
  // backdrop, none of which touch state.user.error on their own, and
  // OverlayPanel's unmountOnExit doesn't unmount synchronously (it's tied to
  // a CSS transition timer), so clearing on close can't be relied on either —
  // clearing on mount is what guarantees every fresh open starts clean.
  useLayoutEffect(() => {
    dispatch(cleanUserError());
  }, [dispatch]);

  const handleCancel = () => {
    setSelectedFile(null);
    setFileError(null);
    close();
  };

  return (
    <Container>
      <Eyebrow>Identity verification</Eyebrow>
      <Title>ID Verification</Title>
      <Description>
        Your physician requires a photo of your ID for patient verification before your
        consultation can be finalized.
      </Description>
      <Checklist>
        <ChecklistItem>
          <Check size={18} weight="bold" />
          Driver's license
        </ChecklistItem>
        <ChecklistItem>
          <Check size={18} weight="bold" />
          Passport
        </ChecklistItem>
        <ChecklistItem>
          <Check size={18} weight="bold" />
          State or military ID
        </ChecklistItem>
      </Checklist>
      {isWebcamMode ? (
        <CameraCapture onCapture={handleWebcamCapture} onCancel={handleWebcamCancel} />
      ) : (
        <>
          <Dropzone
            $isDragActive={isDragActive}
            onClick={handleBrowseClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            data-testid="identity-verification-dropzone"
          >
            <UploadSimple size={32} weight="regular" />
            {selectedFile ? (
              <DropzoneTitle>{selectedFile.name}</DropzoneTitle>
            ) : (
              <>
                <DropzoneTitle>Drag a photo here, or browse</DropzoneTitle>
                <DropzoneSubtext>JPG, PNG or PDF · up to 100MB</DropzoneSubtext>
              </>
            )}
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={handleInputChange}
              onClick={(e) => e.stopPropagation()}
              data-testid="identity-verification-file-input"
            />
          </Dropzone>
          <CameraToggle
            type="button"
            onClick={handleUseCameraClick}
            disabled={isSubmitting}
            data-testid="identity-verification-camera-toggle"
          >
            Use your camera instead
          </CameraToggle>
        </>
      )}
      {fileError && (
        <Feedback isInvalid data-testid="identity-verification-file-error">
          {fileError}
        </Feedback>
      )}
      {error.message && (
        <Feedback isInvalid data-testid="identity-verification-submit-error">
          {error.message}
        </Feedback>
      )}
      <HipaaNote>
        <LockSimple size={18} weight="regular" />
        <Description>Encrypted in transit and stored per HIPAA.</Description>
      </HipaaNote>
      <Actions>
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleCancel();
          }}
        >
          Cancel
        </Link>
        <Button
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={!selectedFile || isSubmitting}
        >
          Submit
        </Button>
      </Actions>
    </Container>
  );
};

export default IdentityVerificationPanelContent;
